import { useState } from "react";
import { DialogTitle } from "@headlessui/react";

import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import type { CareplanCartDataModel, DraftCart } from "@/domains/careplan/models/types";
import { useUpdateSubscription } from "@/domains/careplan/mutations/use-update-subscription";
import { CareplanCartDiscountCodeForm } from "@/routes/_private/-components/cart/careplan-cart-discount-code-form";
import { CareplanCartSubscriptionSummaryCard } from "@/routes/_private/-components/cart/careplan-cart-subscription-summary";
import { PaymentInformationCard } from "@/routes/_private/-components/cart/payment-information-card";
import { Button, type ButtonProps, icons, Modal } from "@/ui";

type UpdateSubscriptionModalProps = Omit<ButtonProps, "children"> & {
  cart: NonNullable<CareplanCartDataModel>;
  draftCart: DraftCart;
};

export const UpdateSubscriptionModal = ({
  cart,
  draftCart,
  ...props
}: UpdateSubscriptionModalProps) => {
  const { id, subscription } = useCareplanContext();
  const { isPending, mutate: updateSubscription } = useUpdateSubscription();
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  if (!subscription) {
    return null;
  }

  const { nextBillingDate, paymentMethod, shippingAddress } = subscription;

  const handleUpdateSubscription = () => {
    updateSubscription(
      {
        careplanId: id,
        cartId: cart.id,
      },
      {
        onSuccess: () => {
          setIsSubscriptionModalOpen(false);
        },
      },
    );
  };

  const handleClose = () => {
    if (isPending) {
      return;
    }

    return setIsSubscriptionModalOpen(false);
  };

  return (
    <>
      <Modal
        className="max-h-[80vh] overflow-auto"
        closeModal={handleClose}
        isOpen={isSubscriptionModalOpen}
      >
        <div className="flex flex-col gap-6 p-6">
          <DialogTitle as="h2" className="py-6 text-xl font-bold text-content sm:text-3xl">
            Confirm Subscription Update
          </DialogTitle>
          <CareplanCartSubscriptionSummaryCard cart={cart} draftCart={draftCart} />

          <CareplanCartDiscountCodeForm cart={cart} />

          {paymentMethod.lastDigits ? (
            <PaymentInformationCard
              icon={<icons.CreditCardIcon className="size-5" />}
              title="Payment Method"
              value={`•••• •••• •••• ${paymentMethod.lastDigits}`}
            />
          ) : null}

          {nextBillingDate ? (
            <PaymentInformationCard
              icon={<icons.CalendarIcon className="size-5" />}
              title="Next Billing Date"
              value={nextBillingDate}
            />
          ) : null}

          {shippingAddress ? (
            <PaymentInformationCard
              icon={<icons.MapPinIcon className="size-5" />}
              title="Shipping Address"
              value={shippingAddress}
            />
          ) : null}

          <p className="text-center font-semibold">
            You can edit payment method, billing date, and address in your subscription manager
            after updating.
          </p>
          <div className="flex flex-col gap-4">
            <Button
              className="justify-center"
              isLoading={isPending}
              onClick={handleUpdateSubscription}
            >
              Confirm Update
            </Button>
            <Button
              className="justify-center font-light text-content-muted underline"
              disabled={isPending}
              onClick={handleClose}
              size="tight"
              variant="focus"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Button
        className="w-full justify-center"
        onClick={() => {
          return setIsSubscriptionModalOpen(true);
        }}
        {...props}
      >
        Update Subscription
      </Button>
    </>
  );
};
